'use client';

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { api } from '@/lib/api';
import type { MarketOverview, MarketBreadth } from '@glintfans/shared';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatCap, formatVolume } from '@/lib/format';

const FNG_LABEL_ZH: Record<string, string> = {
  'extreme fear': '极度恐惧',
  fear: '恐惧',
  neutral: '中性',
  greed: '贪婪',
  'extreme greed': '极度贪婪',
};

export default function OverviewCards() {
  const { language } = useAppStore();
  const t = translate(language);
  const [showMore, setShowMore] = useState(false);
  // D14: 手机首屏只保留两项核心摘要，其余收入「市场概览」折叠区；
  // 桌面（sm+）四卡一行直接展示。
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<MarketOverview>('/market/overview'),
    refetchInterval: 1_000,
  });

  const o = data;
  const fng = o?.fearGreedIndex ?? 50;
  const fngLabel = o ? (language !== 'en' ? FNG_LABEL_ZH[o.fearGreedLabel.toLowerCase()] ?? o.fearGreedLabel : o.fearGreedLabel) : '—';
  const fngColor = fng >= 60 ? 'text-gf-positive' : fng >= 40 ? 'text-gf-warn' : 'text-gf-negative';

  const { data: breadth } = useQuery({
    queryKey: ['breadth'],
    queryFn: () => api.get<MarketBreadth | null>('/market-extra/breadth'),
    refetchInterval: 30_000,
  });

  const { data: gd } = useQuery({
    queryKey: ['derivatives', 'global'],
    queryFn: () =>
      api.get<{
        totalOpenInterestUsd: number;
        longShortRatio: number;
        avgFundingRate: number;
        stablecoinMarketCapUsd: number;
        liquidationUsd24h: number | null;
        hlLiquidationUsd24h: number;
      }>('/market/derivatives/global'),
    refetchInterval: 30_000,
  });

  const bucketTotals = breadth?.buckets.reduce((s, b) => s + b.count, 0) ?? 0;

  function Card({ label, children, sub }: { label: string; children: ReactNode; sub?: ReactNode }) {
    return (
      <div className="card flex min-h-[80px] flex-col justify-between !p-3 sm:min-h-[104px]">
        <div className="text-[10px] leading-tight text-gf-muted sm:text-[11px]">{label}</div>
        <div className="mt-1">{children}</div>
        {sub && <div className="mt-1 truncate text-[9px] text-gf-muted">{sub}</div>}
      </div>
    );
  }

  const card1 = (
    <Card label={t.markets.totalCap} sub={t.markets.volume24h}>
      <div className="text-[15px] font-bold leading-tight tracking-tight sm:text-base" title={o?.totalMarketCapUsd != null ? `$${o.totalMarketCapUsd.toLocaleString('en-US')}` : undefined}>
        {formatCap(o?.totalMarketCapUsd)}
      </div>
      <div className="mt-0.5 text-[12px] text-gf-muted">{formatVolume(o?.totalVolume24hUsd)}</div>
    </Card>
  );
  const card2 = (
    <Card label={`${t.markets.btcDominance} / ${t.markets.ethDominance}`}>
      <div className="text-[15px] font-bold leading-tight tracking-tight sm:text-base">
        {o ? `${o.btcDominance.toFixed(2)}%` : '—'}
      </div>
      <div className="mt-0.5 text-[12px] text-gf-muted">{o ? `${o.ethDominance.toFixed(2)}%` : '—'}</div>
    </Card>
  );
  const card3 = (
    <Card label={t.markets.fearGreed}>
      <div className={`text-[15px] font-bold leading-tight tracking-tight sm:text-base ${fngColor}`}>
        {o ? `${o.fearGreedIndex} · ${fngLabel}` : '—'}
      </div>
      <div className="relative mt-1 h-1 w-full max-w-[120px] rounded-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500">
        <div
          className="absolute -top-[2.5px] h-[8px] w-[8px] rounded-full border border-white bg-white shadow"
          style={{ left: `calc(${fng}% - 4px)` }}
        />
      </div>
    </Card>
  );
  const card4 = (
    <Card label={`${t.markets.ovTotalOi} / ${t.markets.ovLiquidation}`}>
      <div className="text-[15px] font-bold leading-tight tracking-tight sm:text-base">{formatVolume(gd?.totalOpenInterestUsd)}</div>
      <div className="mt-0.5 text-[12px] text-gf-negative">
        {gd?.liquidationUsd24h ? formatVolume(gd.liquidationUsd24h) : '—'}
        {gd?.hlLiquidationUsd24h ? (
          <span className="text-[9px] text-gf-muted"> ({language !== 'en' ? '含链上' : 'on-chain'} {formatVolume(gd.hlLiquidationUsd24h)})</span>
        ) : null}
      </div>
    </Card>
  );

  const moreGrid = (
    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
      <Card label={`${t.markets.ovLongShort} / ${t.markets.ovAvgFunding}`}>
        <div className="text-[14px] font-semibold leading-tight">
          {gd && gd.longShortRatio > 0 ? gd.longShortRatio.toFixed(2) : '—'} /{' '}
          {gd && gd.avgFundingRate !== 0 ? (gd.avgFundingRate * 100).toFixed(4) + '%' : '—'}
        </div>
      </Card>
      <Card label={t.markets.ovStablecap}>
        <div className="text-[14px] font-semibold leading-tight">{formatCap(gd?.stablecoinMarketCapUsd)}</div>
      </Card>
      <Card label={t.markets.breadth}>
        {breadth ? (
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[14px] font-bold text-gf-positive">{breadth.up}</span>
              <span className="text-[9px] text-gf-muted">↑</span>
              <span className="text-[14px] font-bold text-gf-negative">{breadth.down}</span>
              <span className="text-[9px] text-gf-muted">↓</span>
              {breadth.flat ? <span className="text-[14px] font-bold text-gf-muted">{breadth.flat}</span> : null}
            </div>
            {breadth.buckets.length > 0 && bucketTotals > 0 && (
              <div className="mt-1 flex h-1 w-full overflow-hidden rounded-full">
                {breadth.buckets.map((b, i) => (
                  <div
                    key={b.label}
                    className={i >= 3 ? 'bg-gf-negative/70' : 'bg-gf-positive/70'}
                    style={{ width: `${(b.count / bucketTotals) * 100}%` }}
                    title={`${b.label}: ${b.count}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-[14px] font-semibold">—</div>
        )}
      </Card>
      <Card label={language !== 'en' ? '中位涨跌' : 'Median Change'}>
        <div className="text-[14px] font-semibold leading-tight">
          {breadth?.medianChangePct != null
            ? `${breadth.medianChangePct >= 0 ? '+' : ''}${breadth.medianChangePct.toFixed(2)}%`
            : '—'}
        </div>
      </Card>
    </div>
  );

  return (
    <div>
      {/* 核心摘要：手机 2 卡（首屏），桌面 4 卡一行 */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {card1}
        {card2}
        {/* 桌面直接显示 恐惧贪婪 / OI·爆仓 两卡（sm:contents 参与 4 列网格）；手机隐藏到折叠区 */}
        <div className="hidden sm:contents">{card3}{card4}</div>
      </div>

      {/* 手机：其余收入「市场概览」折叠区 */}
      <div className="sm:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="mt-2 flex w-full items-center justify-between rounded-md border border-gf-border/60 bg-gf-surface2/50 px-3 py-2 text-xs text-gf-muted hover:text-gf-accent"
        >
          <span>{t.markets.overviewCollapse}</span>
          <span className={`transition-transform ${mobileOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>
        {mobileOpen && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {card3}
            {card4}
          </div>
        )}
      </div>

      {/* 更多市场数据（双端均可用；手机端随「市场概览」折叠区之后展示） */}
      <button
        type="button"
        onClick={() => setShowMore((v) => !v)}
        className="mt-2 flex items-center gap-1 text-[11px] text-gf-muted hover:text-gf-accent"
      >
        <span>{t.markets.moreData}</span>
        <span className={`transition-transform ${showMore ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {showMore && moreGrid}
    </div>
  );
}
